'use client'

import * as React from 'react'
import type { EmojiClickData } from 'emoji-picker-react'
import EmojiPicker from 'emoji-picker-react'
import * as Popover from 'primitives/popover'
import { Button } from '@repo/design-system-cf/components/button'
import { IconAddPhotoAlternate, IconAddReaction } from '@repo/design-system-cf/components/icons'
import { tw } from 'utils/classnames'

interface Props {
  value: string
  onChange: (v: string) => void
  onImageUploadingChange?: (val: boolean) => void
  onTriggerImageUpload: () => void
  placeholder?: string
  minHeight?: number
  maxHeight?: number
  imageCount?: number
  maxImageCount?: number
  isError?: boolean
}

export const RichTextInput: React.FC<Props> = ({
  value,
  onChange,
  onTriggerImageUpload,
  placeholder = 'Type your text…',
  minHeight = 80,
  maxHeight = 300,
  imageCount,
  maxImageCount = 10,
  isError = false,
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  const [isActive, setIsActive] = React.useState(false)

  const handleEmojiClick = (emoji: EmojiClickData) => {
    const textarea = textareaRef.current
    if (!textarea) {
      return
    }

    const start = textarea.selectionStart
    const end = textarea.selectionEnd

    const newValue = value.substring(0, start) + emoji.emoji + value.substring(end)

    onChange(newValue)

    requestAnimationFrame(() => {
      textarea.focus()
      const caretPos = start + emoji.emoji.length
      textarea.setSelectionRange(caretPos, caretPos)
    })

    setIsActive(true)
  }

  const handleFocus = () => {
    setIsActive(true)
  }

  const handleBlur = () => {
    setIsActive(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setIsActive(true)
    onChange(e.target.value)
  }

  const disableEmojiPicker = imageCount !== undefined && imageCount >= maxImageCount

  return (
    <div
      className={tw(
        'flex flex-col bg-white w-full max-w-[816px] rounded-2 border transition-colors',
        'border-[#1313151F] hover:border-[#13131585]',
        isActive && 'border-[#13131585]',
        isError && 'border-cf-red-600',
      )}
    >
      <div className="flex items-center border-b border-[#1313151F] bg-cf-grey-50 px-4 py-3 gap-3 rounded-t-2">
        <Button
          variant="ghost"
          size="small"
          className="!h-[26px] !w-[26px] p-0 flex items-center justify-center"
          onClick={onTriggerImageUpload}
          iconLeft={<IconAddPhotoAlternate className="text-cf-purple-900 !size-4" />}
          disabled={disableEmojiPicker}
          data-testid="rich-text-input-add-photo-button"
        />

        <Popover.Root>
          <Popover.Trigger asChild>
            <Button
              variant="ghost"
              size="small"
              className="!h-[26px] !w-[26px] p-0 flex items-center justify-center"
              iconLeft={<IconAddReaction className="text-cf-purple-900 !size-4" />}
            />
          </Popover.Trigger>

          <Popover.Content className="z-50">
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              skinTonesDisabled
              searchDisabled
              allowExpandReactions={false}
              previewConfig={{ showPreview: false }}
            />
          </Popover.Content>
        </Popover.Root>
      </div>

      <div className="flex flex-col w-full px-3 pt-3 pb-3 gap-3 bg-cf-white rounded-b-2">
        <textarea
          ref={textareaRef}
          placeholder={placeholder}
          value={value}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          data-testid="rich-text-input-textarea"
          className="w-full resize-none bg-cf-white text-[14px] leading-5 text-[#131315] outline-none placeholder:text-cf-grey-500 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
          style={{ minHeight, maxHeight }}
        />
      </div>
    </div>
  )
}
